import type { CostOverrideMode } from "@repo/shared/types";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./cost-override-dialog.i18n";

export type CostOverrideDialogTarget = {
  /** The fee and, for a product fee, the product: "Management fee · Global Index". */
  label: string;
  unit: "pct" | "amount";
  /** The unit suffix shown beside the input: "%" or the currency. */
  unitLabel: string;
  /** The value in force now (the override, or the catalogue value). */
  value: number | null;
  /** The catalogue value, formatted. */
  catalogueLabel: string;
  mode: CostOverrideMode;
  note: string | null;
  /** Set when the fee is overridden; Reset is offered then. */
  overridden: boolean;
  /**
   * The fee no longer takes new overrides (its mode is off, or the tenant's
   * feature is), but this one still applies: only Reset is offered.
   */
  resetOnly: boolean;
};

type CostOverrideDialogProps = {
  target: CostOverrideDialogTarget | null;
  onClose: () => void;
  onSave: (value: number, note: string | null) => Promise<void>;
  onReset: () => Promise<void>;
  isPending: boolean;
  /** The server's refusal, shown above the form. */
  error: string | null;
};

const PERCENT_MAX = 100;

/**
 * QL-20 — the advisor overrides one fee for this advice session. The rules
 * (mode, note, bounds) are the server's; the form checks them early for UX.
 */
export function CostOverrideDialog({
  target,
  onClose,
  onSave,
  onReset,
  isPending,
  error,
}: CostOverrideDialogProps) {
  const t = useT(T);
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setValue(target.value === null ? "" : String(target.value));
    setNote(target.note ?? "");
    setFormError(null);
  }, [target]);

  const noteRequired = target?.mode === "note_required";
  const resetOnly = target?.resetOnly ?? false;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    const parsed = Number(value.trim().replace(",", "."));
    const max = target.unit === "pct" ? PERCENT_MAX : Number.POSITIVE_INFINITY;
    if (value.trim() === "" || !Number.isFinite(parsed) || parsed < 0 || parsed > max) {
      setFormError(target.unit === "pct" ? t.invalidPercent : t.invalidValue);
      return;
    }
    const trimmedNote = note.trim();
    if (noteRequired && trimmedNote === "") {
      setFormError(t.noteMissing);
      return;
    }
    setFormError(null);
    await onSave(parsed, trimmedNote === "" ? null : trimmedNote);
  };

  const shownError = formError ?? error;

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogScrollContent className="max-w-lg">
        <DialogScrollHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            {shownError && (
              <div
                role="alert"
                className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              >
                {shownError}
              </div>
            )}

            <p className="text-sm font-medium">{target?.label}</p>

            {resetOnly && target && (
              <div className="space-y-1 text-sm">
                <p>{t.resetOnlyHint}</p>
                <p className="text-muted-foreground">{t.catalogueValue(target.catalogueLabel)}</p>
              </div>
            )}

            <div className={resetOnly ? "hidden" : "space-y-2"}>
              <Label htmlFor="cost-override-value">{t.value}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="cost-override-value"
                  type="number"
                  step="any"
                  min="0"
                  max={target?.unit === "pct" ? PERCENT_MAX : undefined}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={isPending}
                  className="w-36"
                  required={!resetOnly}
                />
                <span className="text-muted-foreground">{target?.unitLabel}</span>
              </div>
              {target && (
                <p className="text-xs text-muted-foreground">
                  {t.catalogueValue(target.catalogueLabel)}
                </p>
              )}
            </div>

            <div className={resetOnly ? "hidden" : "space-y-2"}>
              <Label htmlFor="cost-override-note">{noteRequired ? t.noteRequired : t.note}</Label>
              <Textarea
                id="cost-override-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t.notePlaceholder}
                maxLength={2000}
                rows={3}
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">{t.noteHint}</p>
            </div>
          </DialogBody>

          <DialogScrollFooter className="flex flex-wrap justify-between gap-2">
            <div>
              {target?.overridden && (
                <Button type="button" variant="ghost" onClick={onReset} disabled={isPending}>
                  {t.reset}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                {t.cancel}
              </Button>
              {!resetOnly && (
                <Button type="submit" loading={isPending}>
                  {t.save}
                </Button>
              )}
            </div>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
